import React, {useEffect} from 'react';
import {FormattedMessage} from 'react-intl';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Button} from '@material-ui/core';
import {GroupKey, LabelView, LabelViewerProps} from './LabelView';
import {GridViewerVirtualized, GridViewProps} from './GridViewer';
import {ListViewerVirtualized, ListViewerProps} from './ListViewer';
import {BINARY_TYPES} from '../../../types/dataService/DatasetMetadata';
import {FileViewerHeaderLabel} from './FileViewerHeaderLabel';
import ArrowLeftIcon from '../../../assets/icons/ArrowLeftIcon';
import {StudioContentLayout} from '../../StudioContentLayout';
import {GridOnItemsRenderedProps, ListOnItemsRenderedProps} from 'react-window';
import {StudioDatasetViewerProps} from '../StudioDatasetViewer';
import './FileViewer.scss';

// TODO: Have to parametarized this
const LABEL_FILE_COUNT = 20;

const DEFAULT_COLUMNS = [
  {id: 'select', label: '', sortable: false, width: 55},
  {id: 'image', label: '', sortable: false, width: 55},
  {id: 'filename', label: 'File Name', sortable: true},
  {id: 'label', label: 'Label', sortable: false, width: 120},
  {id: 'lastEdited', label: 'Last Edited', sortable: true, width: 200},
  {id: 'dimension', label: 'Dimensions', sortable: false, width: 100, numeric: true},
  {id: 'size', label: 'File Size', sortable: true, width: 120, numeric: true},
];

export type SortId = string;
export type SortDirection = 'asc' | 'desc';

type ReducerState = {
  page: number;
  sort: SortId;
  sortDir: SortDirection;
  label: string | null;
};

const initialState: ReducerState = {
  page: 1,
  sort: 'label',
  sortDir: 'asc',
  label: null,
};
const reverseSort = (sortDir: SortDirection) => (sortDir === 'asc' ? 'desc' : 'asc');

const viewerSlice = createSlice({
  name: 'file/viewer',
  initialState,
  reducers: {
    setLabel(state, action: PayloadAction<string | null>) {
      state.label = action.payload;
      state.page = 1;
    },
    setPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    toggleSortHeading(state, action: PayloadAction<SortId>) {
      if (action.payload === state.sort) {
        state.sortDir = reverseSort(state.sortDir);
      } else {
        state.sort = action.payload;
      }
      state.page = 1;
    },
  },
});

export type FileStyle = {
  color: string;
  background: string;
};
export type FilterProps = {[key: string]: string};
export type LabelHeaderProps = {[key: string]: string};
export type LabelHeaderCountProps = {[key: string]: number};

export type ItemType = {
  id: string;
  filename: string;
  size: number;
  selected?: boolean;
  lastEdited: Date;
  file?: File;
  url?: string;
  label?: string;
  type?: string;
  binaryType?: BINARY_TYPES;
  height?: number;
  width?: number;
  image?: any;
  group?: number;
};

export type ViewId = 'grid' | 'list' | 'label';

export type InfiniteScrollProps = GridOnItemsRenderedProps | ListOnItemsRenderedProps;

export type InfiniteScrollGridEvent = {
  onScrollStop?: (args: InfiniteScrollProps) => void;
};

export type StudioFileItemGroup = {
  label?: string;
  list: ItemType[];
  totalElements: number;
  totalPages?: number;
};

export type StudioFileItemParamProps = Pick<ItemType, 'selected' | 'label'> & {
  id: string;
  type: string;
  isLocal?: boolean;
  style?: FileStyle;
  selectable?: boolean;
  url?: string | File;
  item: ItemType;
  onSelect?: (item: ItemType) => void;
  onClick?: (item: ItemType) => void;
};

export type StudioFileViewerProps = {
  height?: number;
  width?: number;

  itemGroup?: StudioFileItemGroup[];
  itemList?: ItemType[];
  page: number;
  goBackLabel?: string;
  labelHeader?: LabelHeaderProps;
  labelHeaderCount?: LabelHeaderCountProps;
  selections?: string[];
  view: ViewId;
  groupByKey?: GroupKey;
  allowSelection?: boolean;
  virtualizedRowHeight?: number;
  virtualizedColumnWidth?: number;

  gridThumbnailSize?: number;
  listThumbnailSize?: number;

  renderAdornment?: (
    config: Pick<ItemType, 'selected' | 'label'> & {isLocal?: boolean; style?: FileStyle},
    view: ViewId
  ) => React.ReactNode;
  imageRenderer?: (config: StudioFileItemParamProps) => React.ReactNode;

  gridViewRender?: null | ((props: GridViewProps) => React.ReactNode);
  listViewRender?: null | ((props: ListViewerProps) => React.ReactNode);
  labelViewRender?: null | ((props: LabelViewRendererProps) => React.ReactNode);

  onSelectAll?: (value: boolean, ids: string[]) => void;
  onSelect?: (item: ItemType) => void;
  onClick?: (item: ItemType) => void;
  onSort?: (colId: string, dir: string) => void;
  onFilter?: (filter: FilterProps | null, isSubView: boolean) => void;
  loadMore?: () => void;
} & InfiniteScrollGridEvent;

const {setLabel, setPage, toggleSortHeading} = viewerSlice.actions;

export const FileViewer = ({
  itemGroup = [],
  itemList = [],
  page = 1,
  labelHeader,
  goBackLabel,
  selections,
  view,
  virtualizedRowHeight = 100,
  virtualizedColumnWidth = 100,
  gridThumbnailSize = 93,
  allowSelection = true,

  onSelectAll = () => {},
  onSelect,
  onClick,
  imageRenderer,
  gridViewRender = GridViewerVirtualized,
  listViewRender = ListViewerVirtualized,
  labelViewRender = LabelViewRenderer,

  onSort = () => {},
  onFilter = () => {},
  loadMore = () => {},
  onScrollStop = () => {},
}: StudioFileViewerProps) => {
  const [state, dispatch] = React.useReducer(viewerSlice.reducer, initialState);
  const {sort, sortDir, label} = state;
  const isLabelSet = label !== null;
  const isLabelView = view === 'label' && !isLabelSet;

  const gridThumbnailSizeParam = `previewDimensions=${gridThumbnailSize}x${gridThumbnailSize}`;

  useEffect(() => {
    dispatch(setPage(1));
    dispatch(setLabel(null));
  }, [view]);

  const onLocalSort = (id: SortId) => {
    onSort(sort, reverseSort(sortDir));
    dispatch(toggleSortHeading(id));
  };

  const onLabelClick = (id: string, label: string) => {
    dispatch(setLabel(label));
    onFilter({id, label}, true);
  };

  const onLabelRemove = () => {
    onFilter(null, false);
    dispatch(setLabel(null));
  };

  const renderViewer = () => {
    const group = itemGroup[0];
    if (!group?.list.length) {
      return <div className="file-viewer__empty">No images found</div>;
    } else if ((view === 'grid' || isLabelSet) && itemGroup.length) {
      return (
        <>
          <StudioContentLayout
            className="file-viewer"
            header={
              isLabelSet ? (
                <>
                  <Button
                    color="primary"
                    startIcon={<ArrowLeftIcon />}
                    onClick={onLabelRemove}
                  >
                    {goBackLabel ?? <FormattedMessage id="dataViewer.allLabels" />}
                  </Button>
                  <FileViewerHeaderLabel
                    showSelector={false}
                    label={
                      isLabelSet ? label : labelHeader && label && labelHeader[label]
                    }
                    count={group?.totalElements || 0}
                    selected={selections?.length || 0}
                  />
                </>
              ) : (
                undefined
              )
            }
          >
            {() =>
              gridViewRender?.({
                items: itemList,
                selections,
                rowHeight: virtualizedRowHeight,
                columnWidth: virtualizedColumnWidth,
                imageRenderer: ({item, ...rest}) =>
                  imageRenderer?.({
                    ...rest,
                    item,
                    onClick: () => onClick && onClick(item),
                  }),
                totalData: group.totalElements,
                loadMore,
                onScrollStop,
              })
            }
          </StudioContentLayout>
        </>
      );
    } else if (view === 'list') {
      const group = itemGroup[0];
      return listViewRender?.({
        columns: DEFAULT_COLUMNS,
        items: itemList,
        selections,
        onSort: onLocalSort,
        sort,
        sortDir,
        onSelect,
        onClick,
        onSelectAll,
        totalData: group.totalElements,
        loadMore,
        onScrollStop,
        allowSelection,
      });
    } else if (isLabelView) {
      return labelViewRender?.({
        labels: itemGroup,
        limit: LABEL_FILE_COUNT,
        selections,
        allowSelection,
        onLabelClick,
        onSelectAll,
        thumbnailSize: gridThumbnailSizeParam,
        imageRenderer: ({item, ...rest}) =>
          imageRenderer?.({
            ...rest,
            item,
            onSelect: () => onSelect && onSelect(item),
            onClick: () => onClick && onClick(item),
          }),
        labelNameMapper: val => (labelHeader ? labelHeader[val] : val),
      });
    }
  };

  return <>{renderViewer()}</>;
};

export type ListViewRendererProps = Pick<
  ListViewerProps,
  // 'columns' | 'items' | 'selections' | 'onSelectAll' | 'onSort' | 'sort' | 'sortDir'
  'columns' | 'imageRenderer' | 'items' | 'selections'
>;

export type LabelViewGroup = {
  label: string;
  count: number;
  files: ItemType[];
};

export type LabelViewRendererProps = Pick<
  LabelViewerProps,
  'labels' | 'labelNameMapper' | 'limit' | 'selections' | 'onSelectAll' | 'onLabelClick'
> &
  Pick<StudioDatasetViewerProps, 'allowSelection'> & {
    thumbnailSize: string;
    imageRenderer?: (config: StudioFileItemParamProps) => React.ReactNode;
    labelHeader?: Pick<LabelHeaderProps, 'labelHeader'>;
    onSelect?: (item: ItemType) => void;
    onClick?: (item: ItemType) => void;
  };

const DEFAULT_LABEL_MAPPER = (val: string) => val;

export const LabelViewRenderer = ({
  imageRenderer,
  labelHeader,
  thumbnailSize,
  selections,
  onSelect,
  onClick,
  labelNameMapper = DEFAULT_LABEL_MAPPER,
  ...rest
}: LabelViewRendererProps) => (
  <LabelView {...rest} labelNameMapper={labelNameMapper} selections={selections}>
    {(label, item) => {
      const isLocal = !!item.file;
      const selected = selections?.includes(item.id) || false;
      return imageRenderer?.({
        id: String(item.id),
        type: item.type || '',
        selected,
        label,
        isLocal,
        url: item.file || `${item.url}&${thumbnailSize}`,
        item,
        onSelect: () => onSelect && onSelect(item),
        onClick: () => onClick && onClick(item),
      });
    }}
  </LabelView>
);
